<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useStartModularGeneration,
  useGetFieldObservations,
  useGetFieldParts,
  useStartDiagnosticDataExtraction,
  useGetCatalogues,
  useStartIQOAAssociation,
  useGetReportFindings,
  useGetModularGenerationStatus,
  useGetIQOAAssociationStatus,
  useGetReportImages,
  useSmartRenumberReportImages,
  useStartConclusionsGeneration,
  useGetConclusionsGenerationStatus,
} from "@/stores/queries";
import { useQueryClient } from "@tanstack/vue-query";
import { QueryKey } from "@/stores/queries/query-keys";
import AIAssistantButton from "./AIAssistantButton.vue";
import AIMenuPopup from "./AIMenuPopup.vue";
import AIProcessingOverlay from "./AIProcessingOverlay.vue";
import IQOACatalogueSelectModal from "./IQOACatalogueSelectModal.vue";
import IQOAAssociationOverlay from "./IQOAAssociationOverlay.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import { useToast } from "@/composable/useToast";
import type { Diagnostic, Report } from "@/types";

const LOG_PREFIX = "[AIAssistantHub]";

const props = defineProps<{
  diagnosticId: number;
  diagnostic: Diagnostic;
  reportId: number;
  report: Report;
  reportTemplateId: number | undefined;
  showPulse?: boolean;
}>();

const { t } = useTranslation();
const queryClient = useQueryClient();
const { showSuccess } = useToast();

// Refs for props
const diagnosticIdRef = computed(() => props.diagnosticId);
const reportIdRef = computed(() => props.reportId);
const reportTemplateIdRef = computed(() => props.reportTemplateId);

// ==========================================
// Polling - Always active for status tracking
// ==========================================
const shouldPollModular = ref(true);
const shouldPollIQOA = ref(true);
const shouldPollConclusions = ref(true);

// Modular generation status (transcription + report_generation)
const { data: modularStatus, refetch: refetchModularStatus } =
  useGetModularGenerationStatus(
    diagnosticIdRef,
    reportIdRef,
    shouldPollModular,
  );

// IQOA association status
const { data: iqoaStatus, refetch: refetchIQOAStatus } =
  useGetIQOAAssociationStatus(reportIdRef, shouldPollIQOA);

// Conclusions generation status
const { data: conclusionsStatus, refetch: refetchConclusionsStatus } =
  useGetConclusionsGenerationStatus(reportIdRef, shouldPollConclusions);

// ==========================================
// Overlay visibility states
// ==========================================
const showExtractionOverlay = ref(false);
const showReportGenerationOverlay = ref(false);
const showIQOAOverlay = ref(false);
const showRenumberOverlay = ref(false);
const showConclusionsOverlay = ref(false);

// ==========================================
// Photo renumbering state
// ==========================================
type RenumberStep =
  | "analyzing"
  | "overviews"
  | "findings"
  | "applying"
  | "done";
const renumberStep = ref<RenumberStep | null>(null);
const isPhotoRenumbering = ref(false);
const showRenumberConfirm = ref(false);

// ==========================================
// Conclusions generation state (simulated steps on the frontend)
// ==========================================
type ConclusionsStep =
  | "analyzing"
  | "writing_conclusion"
  | "writing_preconisation"
  | "done";
const conclusionsStep = ref<ConclusionsStep | null>(null);
const showConclusionsConfirm = ref(false);

// ==========================================
// Computed states for generation
// ==========================================
const isReportGenerating = computed(
  () => modularStatus.value?.isGenerating ?? false,
);

const isIQOAAssociating = computed(() => {
  const result = iqoaStatus.value?.isProcessing ?? false;
  console.log(`${LOG_PREFIX} isIQOAAssociating computed:`, {
    iqoaStatus: iqoaStatus.value,
    isProcessing: iqoaStatus.value?.isProcessing,
    result,
  });
  return result;
});

const isExtractingData = computed(
  () => props.diagnostic?.isExtractingData ?? false,
);

const isGeneratingConclusions = computed(
  () => conclusionsStatus.value?.isProcessing ?? false,
);

const isAnyGenerating = computed(
  () =>
    isExtractingData.value ||
    isReportGenerating.value ||
    isIQOAAssociating.value ||
    isPhotoRenumbering.value ||
    isGeneratingConclusions.value,
);

const currentStep = computed(() => modularStatus.value?.currentStep ?? null);

const iqoaProgress = computed(
  () =>
    iqoaStatus.value?.progress ?? {
      current: 0,
      total: 0,
    },
);

// ==========================================
// Auto-show/hide overlays based on status
// ==========================================

// Watch report generation status
watch(
  isReportGenerating,
  (generating, wasGenerating) => {
    if (generating && !wasGenerating) {
      // Generation started - show overlay
      showReportGenerationOverlay.value = true;
    }
    if (!generating && wasGenerating) {
      // Generation completed - hide overlay and invalidate queries
      console.log(`${LOG_PREFIX} Report generation completed!`);
      showReportGenerationOverlay.value = false;
      queryClient.invalidateQueries({ queryKey: [QueryKey.SECTIONS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.DIAGNOSTIC_IMAGES] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.REPORT_IMAGES,
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, props.reportId],
      });
    }
  },
  { immediate: true },
);

// Watch IQOA association status
watch(
  isIQOAAssociating,
  (associating, wasAssociating) => {
    if (associating && !wasAssociating) {
      showIQOAOverlay.value = true;
    }
    if (!associating && wasAssociating) {
      console.log(`${LOG_PREFIX} IQOA association completed!`);
      showIQOAOverlay.value = false;
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKey.FINDINGS,
      });
    }
  },
  { immediate: true },
);

// Watch conclusions generation status

const runConclusionsStepSimulation = async () => {
  conclusionsStep.value = "analyzing";
  await wait(1200);
  if (!showConclusionsOverlay.value) return;
  conclusionsStep.value = "writing_conclusion";
  await wait(1500);
  if (!showConclusionsOverlay.value) return;
  conclusionsStep.value = "writing_preconisation";
  // Stays on "writing_preconisation" until the polling detects completion
};

watch(
  isGeneratingConclusions,
  (generating, wasGenerating) => {
    if (generating && !wasGenerating) {
      console.log(`${LOG_PREFIX} Conclusions generation started!`);
      // Only show the overlay here if it's not already visible — the handler
      // may have opened it eagerly (avoiding a polling delay). This guard
      // prevents the step simulation from being launched twice.
      if (!showConclusionsOverlay.value) {
        showConclusionsOverlay.value = true;
        runConclusionsStepSimulation();
      }
    }
    if (!generating && wasGenerating) {
      console.log(`${LOG_PREFIX} Conclusions generation completed!`);
      conclusionsStep.value = "done";
      // Brief delay so user sees the "done" state before overlay closes
      setTimeout(() => {
        showConclusionsOverlay.value = false;
        conclusionsStep.value = null;
      }, 500);
      // Invalidate the report so ReportConclusionsStep re-renders with new content.
      // (The query hook also invalidates, this is a belt-and-suspenders safety.)
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, props.reportId],
      });
      // Surface any error from the backend
      const err = conclusionsStatus.value?.error;
      if (err) {
        console.error(`${LOG_PREFIX} Conclusions generation error:`, err);
      }
    }
  },
  { immediate: true },
);

// Watch document extraction status
let extractionPollingInterval: ReturnType<typeof setInterval> | null = null;

const invalidateExtractionQueries = () => {
  queryClient.invalidateQueries({
    queryKey: [QueryKey.DIAGNOSTICS, props.diagnosticId],
  });
  queryClient.invalidateQueries({
    queryKey: [QueryKey.REPORTS, props.reportId],
  });
};

const startExtractionPolling = () => {
  if (extractionPollingInterval) return;
  extractionPollingInterval = setInterval(invalidateExtractionQueries, 3000);
};

const stopExtractionPolling = () => {
  if (extractionPollingInterval) {
    clearInterval(extractionPollingInterval);
    extractionPollingInterval = null;
  }
};

watch(
  isExtractingData,
  (extracting, wasExtracting) => {
    if (extracting && !wasExtracting) {
      showExtractionOverlay.value = true;
      startExtractionPolling();
    }
    if (!extracting && wasExtracting) {
      console.log(`${LOG_PREFIX} Document extraction completed!`);
      showExtractionOverlay.value = false;
      stopExtractionPolling();
      invalidateExtractionQueries();
    }
  },
  { immediate: true },
);

// Cleanup on unmount
onUnmounted(() => {
  stopExtractionPolling();
  shouldPollModular.value = false;
  shouldPollIQOA.value = false;
  shouldPollConclusions.value = false;
});

// ==========================================
// Popup and UI state
// ==========================================
const popupVisible = ref(false);

// Field observations and parts for enabling AI features
const { data: fieldObservations } = useGetFieldObservations(diagnosticIdRef);
const hasObservations = computed(
  () => (fieldObservations.value?.length ?? 0) > 0,
);
const { data: fieldParts } = useGetFieldParts(diagnosticIdRef);
const hasFieldParts = computed(() => (fieldParts.value?.length ?? 0) > 0);

// Document states
const hasDocument = computed(() => !!props.diagnostic?.document);
const hasSuggestions = computed(() => {
  const suggestions = props.diagnostic?.suggestions;
  return !!(suggestions && Object.keys(suggestions).length > 0);
});

// Count modules that need generation (for badge on FAB)
const modulesNeedingGeneration = computed(() => {
  if (!modularStatus.value) return 0;
  const reportGenStatus = modularStatus.value.modules.report_generation;
  if (
    reportGenStatus.status === "not_started" ||
    reportGenStatus.status === "outdated"
  ) {
    return 1;
  }
  return 0;
});

// ==========================================
// IQOA Association
// ==========================================
const { data: catalogues } = useGetCatalogues();
const { data: reportFindings } = useGetReportFindings(reportIdRef);

const hasFindings = computed(() => (reportFindings.value?.length ?? 0) > 0);
const findingsCount = computed(() => reportFindings.value?.length ?? 0);
const existingAssociationsCount = computed(
  () =>
    reportFindings.value?.filter((f) => f.disorderGradeId !== null).length ?? 0,
);

const showIQOAModal = ref(false);

const iqoaAssociationStatus = computed(
  (): "idle" | "completed" | "outdated" => {
    if (!iqoaStatus.value?.lastCompletedAt) return "idle";
    return "completed";
  },
);

// ==========================================
// Mutations
// ==========================================
const { mutateAsync: startGeneration, isPending: isStartingGeneration } =
  useStartModularGeneration(diagnosticIdRef, reportIdRef);

const { mutateAsync: startDocumentExtraction } =
  useStartDiagnosticDataExtraction(diagnosticIdRef, reportTemplateIdRef);

const { mutateAsync: startIQOAAssociation, isPending: isStartingIQOA } =
  useStartIQOAAssociation(reportIdRef);

const { mutateAsync: smartRenumberPhotos } =
  useSmartRenumberReportImages(reportIdRef);

const { mutateAsync: startConclusionsGeneration } =
  useStartConclusionsGeneration(reportIdRef);

// Report images for enabling renumbering button
const { data: reportImages } = useGetReportImages(reportIdRef);
const hasReportImages = computed(() => (reportImages.value?.length ?? 0) > 0);

// ==========================================
// Overlay step definitions
// ==========================================
const extractionSteps = computed(() => [
  { key: "analyzing", label: t("report:ai.documentExtraction.analyzing") },
]);

const generationSteps = computed(() =>
  (
    [
      "transcribing",
      "analyzing",
      "creating_sections",
      "sorting_photos",
      "generating_findings",
      "assigning_overviews",
    ] as const
  ).map((key) => ({ key, label: t(`report:findings.generation.${key}`) })),
);

const renumberSteps = computed(() =>
  (["analyzing", "overviews", "findings", "applying"] as const).map((key) => ({
    key,
    label: t(`report:photoRenumbering.overlay.steps.${key}`),
  })),
);

const conclusionsSteps = computed(() =>
  (["analyzing", "writing_conclusion", "writing_preconisation"] as const).map(
    (key) => ({
      key,
      label: t(`report:ai.conclusionsGeneration.overlay.steps.${key}`),
    }),
  ),
);

// ==========================================
// Confirmation modal
// ==========================================
const showConfirmModal = ref(false);

// ==========================================
// Event handlers
// ==========================================

// Toggle popup
const togglePopup = () => {
  popupVisible.value = !popupVisible.value;
  if (popupVisible.value) {
    refetchModularStatus();
    refetchIQOAStatus();
    refetchConclusionsStatus();
  }
};

const closePopup = () => {
  popupVisible.value = false;
};

// Document extraction
const handleDocumentExtraction = async () => {
  popupVisible.value = false;
  showExtractionOverlay.value = true;

  try {
    await startDocumentExtraction();
    startExtractionPolling();
  } catch (error) {
    console.error(`${LOG_PREFIX} Document extraction error:`, error);
    showExtractionOverlay.value = false;
  }
};

// Handle click on document extraction module (re-open overlay if in progress)
const handleDocumentExtractionClick = () => {
  if (isExtractingData.value) {
    // Re-open overlay
    showExtractionOverlay.value = true;
    popupVisible.value = false;
  } else {
    handleDocumentExtraction();
  }
};

// Findings generation
const handleFindingsGenerationRequest = () => {
  if (isReportGenerating.value) {
    // Re-open overlay
    showReportGenerationOverlay.value = true;
    popupVisible.value = false;
  } else {
    showConfirmModal.value = true;
  }
};

const handleConfirmGenerate = async () => {
  showConfirmModal.value = false;
  popupVisible.value = false;
  showReportGenerationOverlay.value = true;

  try {
    await startGeneration(["transcription", "report_generation"]);
  } catch (error) {
    console.error(`${LOG_PREFIX} Generation error:`, error);
    showReportGenerationOverlay.value = false;
  }
};

// IQOA association
const handleIQOAAssociationRequest = () => {
  if (isIQOAAssociating.value) {
    // Re-open overlay
    showIQOAOverlay.value = true;
    popupVisible.value = false;
  } else {
    popupVisible.value = false;
    showIQOAModal.value = true;
  }
};

const handleConfirmIQOA = async (payload: {
  catalogueIds: number[];
  replaceExisting: boolean;
}) => {
  showIQOAModal.value = false;
  showIQOAOverlay.value = true;

  try {
    await startIQOAAssociation(payload);
  } catch (error) {
    console.error(`${LOG_PREFIX} IQOA association error:`, error);
    showIQOAOverlay.value = false;
  }
};

// Photo renumbering
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// "appearance" = ordre d'apparition (IA) ; "identifier" = reprendre le numéro
// identifiant (A) de chaque photo comme numéro rapport (B).
const renumberMode = ref<"appearance" | "identifier">("appearance");

const handlePhotoRenumberingRequest = () => {
  if (isPhotoRenumbering.value) {
    // Re-open overlay if a renumbering is already in progress
    showRenumberOverlay.value = true;
    popupVisible.value = false;
  } else {
    popupVisible.value = false;
    showRenumberConfirm.value = true;
  }
};

// L'utilisateur choisit le mode via l'une des 2 cartes de la modal.
const chooseRenumberMode = (mode: "appearance" | "identifier") => {
  renumberMode.value = mode;
  showRenumberConfirm.value = false;
  handleConfirmPhotoRenumbering();
};

const handleConfirmPhotoRenumbering = async () => {
  showRenumberConfirm.value = false;
  isPhotoRenumbering.value = true;
  showRenumberOverlay.value = true;
  renumberStep.value = "analyzing";

  const MIN_DURATION_MS = 4500;
  const start = Date.now();
  let succeeded = false;

  try {
    await wait(700);
    renumberStep.value = "overviews";
    await wait(900);
    renumberStep.value = "findings";

    // Launch the real API call in parallel with the simulated steps
    const apiCall = smartRenumberPhotos(renumberMode.value);
    await wait(900);
    renumberStep.value = "applying";

    // Wait for the real response
    await apiCall;

    // Guarantee minimum duration so the AI feels like it's "thinking"
    const elapsed = Date.now() - start;
    if (elapsed < MIN_DURATION_MS) {
      await wait(MIN_DURATION_MS - elapsed);
    }

    renumberStep.value = "done";
    succeeded = true;
    await wait(400);
  } catch (error) {
    console.error(`${LOG_PREFIX} Photo renumbering error:`, error);
  } finally {
    showRenumberOverlay.value = false;
    renumberStep.value = null;
    isPhotoRenumbering.value = false;
    if (succeeded) {
      showSuccess("toast:report-images.smart-renumber-success");
    }
  }
};

// Conclusions generation
const hasExistingConclusionsContent = computed(() => {
  const conclusion = props.report?.conclusion?.trim() ?? "";
  const preconisation = props.report?.preconisation?.trim() ?? "";
  return conclusion.length > 0 || preconisation.length > 0;
});

const handleConclusionsGenerationRequest = () => {
  if (isGeneratingConclusions.value) {
    // Re-open overlay if already running
    showConclusionsOverlay.value = true;
    popupVisible.value = false;
  } else {
    popupVisible.value = false;
    showConclusionsConfirm.value = true;
  }
};

const handleConfirmConclusionsGeneration = async () => {
  showConclusionsConfirm.value = false;

  // Show the overlay immediately — don't wait for the polling to detect the
  // isProcessing=true state (there would be up to a 2s delay otherwise).
  // The watch() remains the authority for CLOSING the overlay when the
  // background job completes (polling detects isProcessing=false).
  if (!showConclusionsOverlay.value) {
    showConclusionsOverlay.value = true;
    runConclusionsStepSimulation();
  }

  try {
    await startConclusionsGeneration();
    // Force an immediate status refetch so the polling picks up the new
    // isProcessing=true state without waiting for a natural tick.
    await refetchConclusionsStatus();
  } catch (error) {
    console.error(`${LOG_PREFIX} Conclusions generation error:`, error);
    // Mutation failed — close the overlay we just opened
    showConclusionsOverlay.value = false;
    conclusionsStep.value = null;
  }
};

// Close overlay handlers (user can close overlays manually)
const closeExtractionOverlay = () => {
  showExtractionOverlay.value = false;
};

const closeReportGenerationOverlay = () => {
  showReportGenerationOverlay.value = false;
};

const closeIQOAOverlay = () => {
  showIQOAOverlay.value = false;
};

// Expose method for external triggering (e.g., from banner)
const triggerFindingsGeneration = () => {
  showConfirmModal.value = true;
};

defineExpose({
  triggerFindingsGeneration,
});

// Debug logging
onMounted(() => {
  console.log(`${LOG_PREFIX} Mounted - polling always active`);
});
</script>

<template>
  <!-- Floating AI button -->
  <AIAssistantButton
    :is-generating="isAnyGenerating"
    :modules-count="modulesNeedingGeneration"
    :show-pulse="showPulse && hasObservations && !isAnyGenerating"
    @click="togglePopup"
  />

  <!-- Popup menu -->
  <AIMenuPopup
    :visible="popupVisible"
    :has-document="hasDocument"
    :has-observations="hasObservations"
    :has-field-parts="hasFieldParts"
    :is-extracting-data="isExtractingData"
    :has-suggestions="hasSuggestions"
    :findings-generation-status="modularStatus ?? null"
    :is-report-generating="isReportGenerating"
    :has-findings="hasFindings"
    :has-report-images="hasReportImages"
    :is-photo-renumbering="isPhotoRenumbering"
    :iqoa-association-status="iqoaAssociationStatus"
    :is-associating-iqoa="isIQOAAssociating"
    :is-generating-conclusions="isGeneratingConclusions"
    :conclusions-last-completed-at="conclusionsStatus?.lastCompletedAt ?? null"
    @close="closePopup"
    @document-extraction="handleDocumentExtractionClick"
    @findings-generation="handleFindingsGenerationRequest"
    @photo-renumbering="handlePhotoRenumberingRequest"
    @iqoa-association="handleIQOAAssociationRequest"
    @conclusions-generation="handleConclusionsGenerationRequest"
  />

  <!-- Confirmation modal for report generation -->
  <ConfirmationModal
    v-model="showConfirmModal"
    :title="t('report:findings.generateWithAI.confirmTitle')"
    :message="t('report:findings.generateWithAI.confirmMessage')"
    :confirm-label="t('report:findings.generateWithAI.confirmButton')"
    :loading="isStartingGeneration"
    danger
    @confirm="handleConfirmGenerate"
  />

  <!-- Report Generation overlay -->
  <AIProcessingOverlay
    :visible="showReportGenerationOverlay"
    icon="bi-stars"
    :steps="generationSteps"
    :current-step="currentStep"
    @close="closeReportGenerationOverlay"
  />

  <!-- Document extraction overlay -->
  <AIProcessingOverlay
    :visible="showExtractionOverlay"
    icon="bi-file-earmark-text"
    :steps="extractionSteps"
    current-step="analyzing"
    :message="t('report:ai.documentExtraction.message')"
    @close="closeExtractionOverlay"
  />

  <!-- IQOA Catalogue Selection Modal -->
  <IQOACatalogueSelectModal
    v-model="showIQOAModal"
    :catalogues="catalogues ?? []"
    :findings-count="findingsCount"
    :existing-associations-count="existingAssociationsCount"
    :loading="isStartingIQOA"
    @confirm="handleConfirmIQOA"
  />

  <!-- IQOA Association Overlay -->
  <IQOAAssociationOverlay
    :visible="showIQOAOverlay"
    :progress="iqoaProgress"
    @close="closeIQOAOverlay"
  />

  <!-- Photo renumbering: choix du mode (2 cartes cliquables) -->
  <el-dialog
    v-model="showRenumberConfirm"
    :title="t('report:photoRenumbering.confirmTitle')"
    width="640px"
    append-to-body
  >
    <div class="renumber-choice">
      <button
        type="button"
        class="renumber-card"
        @click="chooseRenumberMode('appearance')"
      >
        <i class="bi-123 renumber-card__icon" />
        <div class="renumber-card__title">
          {{ t("report:photoRenumbering.choice.appearance.title") }}
        </div>
        <div class="renumber-card__desc">
          {{ t("report:photoRenumbering.choice.appearance.description") }}
        </div>
      </button>
      <button
        type="button"
        class="renumber-card"
        @click="chooseRenumberMode('identifier')"
      >
        <i class="bi-upc renumber-card__icon" />
        <div class="renumber-card__title">
          {{ t("report:photoRenumbering.choice.identifier.title") }}
        </div>
        <div class="renumber-card__desc">
          {{ t("report:photoRenumbering.choice.identifier.description") }}
        </div>
      </button>
    </div>
  </el-dialog>

  <!-- Photo renumbering overlay -->
  <AIProcessingOverlay
    :visible="showRenumberOverlay"
    icon="bi-123"
    :title="t('report:photoRenumbering.overlay.title')"
    :steps="renumberSteps"
    :current-step="renumberStep"
    @close="showRenumberOverlay = false"
  />

  <!-- Conclusions generation confirmation modal -->
  <ConfirmationModal
    v-model="showConclusionsConfirm"
    :title="t('report:ai.conclusionsGeneration.confirmTitle')"
    :message="
      hasExistingConclusionsContent
        ? t('report:ai.conclusionsGeneration.confirmMessageOverwrite')
        : t('report:ai.conclusionsGeneration.confirmMessage')
    "
    :confirm-label="t('report:ai.conclusionsGeneration.confirmButton')"
    :danger="hasExistingConclusionsContent"
    @confirm="handleConfirmConclusionsGeneration"
  />

  <!-- Conclusions generation overlay -->
  <AIProcessingOverlay
    :visible="showConclusionsOverlay"
    icon="bi-card-text"
    :title="t('report:ai.conclusionsGeneration.overlay.title')"
    :steps="conclusionsSteps"
    :current-step="conclusionsStep"
    @close="showConclusionsOverlay = false"
  />
</template>

<style scoped lang="scss">
.text-ai {
  color: #8b5cf6;
}

.renumber-choice {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
}

.renumber-card {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1.25rem 1rem;
  text-align: left;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background: var(--bs-body-bg);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;

  &:hover {
    border-color: var(--el-color-primary);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: translateY(1px);
  }

  &__icon {
    font-size: 1.6rem;
    color: var(--el-color-primary);
  }

  &__title {
    font-weight: 600;
    color: var(--bs-body-color);
  }

  &__desc {
    font-size: 0.82rem;
    color: var(--bs-secondary-color);
    line-height: 1.4;
  }
}
</style>
